import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Row, Col, Spin, Empty, Typography, Space, Divider, Affix, Button, Tooltip, Modal, notification } from 'antd';
import { PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useAuthZ } from '../../authZ/useAuthZ';
import PageContent from '../../structure/PageContent';
import { useReleasesLogic } from '../useReleasesLogic';
import { useNotificationsLogic } from '../useNotificationsLogic';
import CreateReleaseModal from './CreateReleaseModal';
import EditReleaseModal from './EditReleaseModal';
import { useI18nLocalization } from '../../i18nLocalization/useI18nLocalization';

const Releases = () => {
	dayjs.extend(utc);

	const authZ = useAuthZ();
	const releasesLogic = useReleasesLogic();
	const notificationsLogic = useNotificationsLogic();

	//-- Used for translations of text
	const { t } = useI18nLocalization();

	//-- Data source
	const [releases, setReleases] = useState(null);
	const [selectedRelease, setSelectedRelease] = useState(null);

	const [createReleaseModalIsVisible, setCreateReleaseModalIsVisible] = useState(false);
	const [editReleaseModalIsVisible, setEditReleaseModalIsVisible] = useState(false);

	useEffect(() => {
		if (!releases) {
			getReleases();
		}
	}, []);

	//-- Get releases from the API
	const getReleases = async () => {
		const rels = await releasesLogic.getReleases();
		setReleases(rels);
	};

	const deleteRelease = async (id) => {
		const successful = await releasesLogic.deleteRelease(id);

		if (successful) {
			getReleases();

			notification['success']({
				message: t(`Release deleted`)
			});
		} else {
			notification['error']({
				message: t('DeleteReleaseError'),
				duration: 10
			});
		}
	};

	return !releases ? (
		<Row justify='center' align='middle' style={{ height: '100%' }}>
			<Col>
				<Spin />
			</Col>
		</Row>
	) : !releases.length ? (
		<>
			<Row justify='center' align='middle' style={{ height: '100%' }}>
				<Col>
					<Empty description={<span>{t('NoReleases')}</span>} />
				</Col>
			</Row>
			{renderNewEditButton(
				authZ,
				setCreateReleaseModalIsVisible,
				createReleaseModalIsVisible,
				getReleases,
				notificationsLogic,
				setSelectedRelease,
				editReleaseModalIsVisible,
				setEditReleaseModalIsVisible,
				selectedRelease,
				t
			)}
		</>
	) : (
		<>
			{_.map(releases, (r) => {
				return (
					<PageContent key={r.id} style={{ marginBottom: '15px' }}>
						<>
							<Row align='middle' justify='space-between'>
								<Col xs={{ span: 20 }}>
									<Typography.Title level={3}>{r.applicationName}</Typography.Title>
								</Col>
								<Col xs={{ span: 4 }}>
									<Typography.Title level={4} italic style={{ float: 'right' }}>
										v{r.version}
									</Typography.Title>
								</Col>
								<Col xs={{ span: 20 }}>
									<Typography.Title level={4}>{r.title}</Typography.Title>
								</Col>
								<Col xs={{ span: 4 }}>
									<Typography.Title level={5} italic style={{ marginTop: '10px', float: 'right' }}>
										{t('{{date}}', { date: dayjs(r.releaseDateTimeUtc).utc().local() })}
									</Typography.Title>
								</Col>
							</Row>

							{authZ.userIsEntDevAdmin() && (
								<>
									<Space direction='horizontal' style={{ marginTop: '10px', float: 'right', paddingLeft: '25px' }}>
										<i
											className='las la-pencil-alt la-lg cursor-pointer'
											onClick={() => {
												setSelectedRelease(r);
												setEditReleaseModalIsVisible(true);
											}}
										></i>
										<i
											className='las la-trash la-lg cursor-pointer'
											onClick={() => {
												Modal.confirm({
													title: `${t('Delete')} '${r.version}: ${r.title}'`,
													icon: <ExclamationCircleOutlined />,
													content: `${t('Are you sure you want to delete this release?')} ${t(
														'This action cannot be undone'
													)}.`,
													okText: `${t('Delete')}`,
													cancelText: `${t('Cancel')}`,
													onOk() {
														deleteRelease(r.id);
													}
												});
											}}
										></i>
									</Space>
								</>
							)}
							<Divider style={{ margin: '0px 0px 15px 0px' }} />
							<Typography.Paragraph style={{ marginBottom: 'unset', whiteSpace: 'pre-wrap' }}>
								{r.notes}
							</Typography.Paragraph>
						</>
					</PageContent>
				);
			})}
			{renderNewEditButton(
				authZ,
				setCreateReleaseModalIsVisible,
				createReleaseModalIsVisible,
				getReleases,
				notificationsLogic,
				setSelectedRelease,
				editReleaseModalIsVisible,
				setEditReleaseModalIsVisible,
				selectedRelease,
				t
			)}
		</>
	);
};

const renderNewEditButton = (
	authZ,
	setCreateReleaseModalIsVisible,
	createReleaseModalIsVisible,
	getReleases,
	notificationsLogic,
	setSelectedRelease,
	editReleaseModalIsVisible,
	setEditReleaseModalIsVisible,
	selectedRelease,
	t
) => {
	return (
		authZ.userIsEntDevAdmin() && (
			<>
				<Affix style={{ position: 'absolute', bottom: '7%', right: '2%' }}>
					<Tooltip title={t('Create a new release')} placement='left'>
						<Button
							type='primary'
							shape='circle'
							size={'large'}
							icon={<PlusOutlined />}
							style={{ boxShadow: '2px 2px 5px grey' }}
							onClick={() => {
								setCreateReleaseModalIsVisible(true);
							}}
						/>
					</Tooltip>
				</Affix>
				<CreateReleaseModal
					isVisible={createReleaseModalIsVisible}
					onSaveCallback={() => {
						getReleases();
						setCreateReleaseModalIsVisible(false);
						setSelectedRelease(null);
						notificationsLogic.refreshCurrentNotifications();
					}}
					onCloseCallback={() => {
						setCreateReleaseModalIsVisible(false);
						setSelectedRelease(null);
					}}
				/>
				<EditReleaseModal
					isVisible={editReleaseModalIsVisible}
					onSaveCallback={() => {
						getReleases();
						setEditReleaseModalIsVisible(false);
						setSelectedRelease(null);
						notificationsLogic.refreshCurrentNotifications();
					}}
					onCloseCallback={() => {
						setEditReleaseModalIsVisible(false);
						setSelectedRelease(null);
					}}
					release={selectedRelease}
				/>
			</>
		)
	);
};

export default Releases;
